import { useEffect, useRef } from 'react';
import favicon from '../../public/favicon.ico';
/**
 * Custom hook to set the document's favicon based on the logo content
 * from the configuration state.
 *
 * It handles both direct image URLs and inline SVG strings by converting the latter
 * into a Data URL.
 *
 * @param header_logo The header logo image URL or SVG string
 * @param shouldShow Whether to show the favicon (default: true)
 */
/**
 * Determines the href and type for a favicon link based on logo content
 */
const getFaviconProperties = (logo) => {
    const trimmedLogo = logo.trim();
    // SVG string
    if (trimmedLogo.startsWith('<svg')) {
        const encodedSvg = encodeURIComponent(trimmedLogo);
        return {
            href: `data:image/svg+xml;charset=utf-8,${encodedSvg}`,
            type: 'image/svg+xml'
        };
    }
    // Base64 data URL
    if (logo.startsWith('data:image/')) {
        const mimePattern = /data:image\/([^;]+)/;
        const mimeMatch = mimePattern.exec(logo);
        return {
            href: logo,
            type: mimeMatch ? `image/${mimeMatch[1]}` : 'image/x-icon'
        };
    }
    // Direct URL - determine type from extension
    const typeMap = {
        '.svg': 'image/svg+xml',
        '.png': 'image/png',
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg'
    };
    const extension = Object.keys(typeMap).find((ext) => logo.endsWith(ext));
    return {
        href: logo,
        type: extension ? typeMap[extension] : 'image/x-icon'
    };
};
/**
 * Creates and appends a favicon link element to the document head
 */
const createFaviconLink = (href, type) => {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = href;
    link.type = type;
    document.getElementsByTagName('head')[0].appendChild(link);
};
/**
 * Removes all existing favicon links from the document head
 */
const removeExistingFavicons = () => {
    const existingLinks = document.querySelectorAll("link[rel*='icon']");
    for (const link of existingLinks) {
        link.remove();
    }
};
const useFavicon = (header_logo, shouldShow = true) => {
    const previousLogoRef = useRef('');
    const hasSetFaviconRef = useRef(false);
    useEffect(() => {
        // If header_logo is empty and we're in a loading state (transitioning between apps),
        // don't change the favicon to prevent flickering
        if (!header_logo && hasSetFaviconRef.current) {
            // Keep existing favicon during transition
            return;
        }
        // Only update if the logo actually changed or it's the first load
        if (header_logo !== previousLogoRef.current ||
            !hasSetFaviconRef.current) {
            removeExistingFavicons();
            if (header_logo && shouldShow) {
                const { href, type } = getFaviconProperties(header_logo);
                createFaviconLink(href, type);
                previousLogoRef.current = header_logo;
            }
            else {
                // Use default favicon.ico from public folder
                createFaviconLink(favicon, 'image/x-icon');
                previousLogoRef.current = '';
            }
            hasSetFaviconRef.current = true;
        }
    }, [header_logo, shouldShow]);
};
export default useFavicon;
